import React, { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useScene } from './hooks/useScene';
import { getBoxMaterials } from './hooks/useInstancedBoxes';
import { createArrowTexture, disposeMaterials } from './utils';
import { useAssets } from '../../context/AssetsContext';
import { PMREMGenerator } from 'three';

interface BoxSceneProps {
	width: number;
	length: number;
	height: number;
	labelPosition?: number;
	showFaceLabels?: boolean;
	zoom?: number;
	enableControls?: boolean;
}

interface FaceLabel {
	text: string;
	width: number;
	height: number;
}

interface FaceConfig {
	width: number;
	height: number;
	pos: THREE.Vector3;
	rot: THREE.Euler;
}

export default function BoxScene({
	width,
	length,
	height,
	labelPosition = -1,
	showFaceLabels,
	zoom = 1,
	enableControls = true,
}: BoxSceneProps) {
	const mountRef = useRef<HTMLDivElement>(null);
	const assets = useAssets();
	const boundingSize = Math.max(width, height, length) * zoom;

	const { sceneRef, rendererRef, frameContent } = useScene({
		mountRef,
		boundingSize: boundingSize * 2.5,
		transparent: true,
		enableControls: enableControls,
		minDistance: 500,
		maxDistance: 1800,
	});

	useEffect(() => {
		if (!sceneRef.current || !rendererRef.current) return;
		const pmremGenerator = new PMREMGenerator(rendererRef.current);
		const envRT = pmremGenerator.fromEquirectangular(assets.hdrTexture!);
		sceneRef.current!.environment = envRT.texture;
		sceneRef.current!.background = null;
		return () => {
			envRT.dispose();
			pmremGenerator.dispose();
		};
	}, [sceneRef, rendererRef]);

	useEffect(() => {
		frameContent();
	}, [width, length, height]);

	const boxMeshRef = useRef<THREE.Mesh | null>(null);
	const textGroupRef = useRef<THREE.Group | null>(null);
	const arrowPlaneRef = useRef<THREE.Mesh | null>(null);

	const getFaceMap = (labelPosition: number): number => {
		const faceMapping: Record<number, number> = {
			1: 1,
			2: 4,
			3: 0,
			4: 5,
		};
		return faceMapping[labelPosition] ?? -1;
	};

	// Create text and arrow textures only once and memoize them
	const textTextures = useMemo(() => {
		if (!showFaceLabels) return [];
	
		const faceLabels: FaceLabel[] = [
			{ text: 'Right\n(Length)', width: height, height: length },
			{ text: 'Left\n(Length)', width: height, height: length },
			{ text: 'Front\n(Width)', width: width, height: height },
			{ text: 'Back\n(Width)', width: width, height: height },
		];
	
		return faceLabels.map(label =>
			createTextTexture(label.text, label.width, label.height)
		);
	}, [showFaceLabels, width, height, length]);

	const arrowTexture = useMemo(() => {
		return showFaceLabels ? createArrowTexture() : null;
	}, [showFaceLabels]);

	// Function to create a text texture for box faces
	function createTextTexture(text: string, faceWidth: number, faceHeight: number): THREE.CanvasTexture {
		const scale = 512; // resolution base
		const aspect = faceWidth / faceHeight;
		const canvas = document.createElement('canvas');
	
		canvas.width = aspect >= 1 ? scale : scale * aspect;
		canvas.height = aspect >= 1 ? scale / aspect : scale;
	
		const ctx = canvas.getContext('2d');
		if (!ctx) return new THREE.CanvasTexture(canvas);
	
		ctx.clearRect(0, 0, canvas.width, canvas.height);
	
		// Adjust font size relative to canvas size
		const fontSize = Math.min(canvas.width, canvas.height) / 6;
		const lineHeight = fontSize * 1.4;
	
		ctx.font = `bold ${fontSize}px Larsseit`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillStyle = '#000000';
	
		const lines = text.split(/\r?\n/);
		const yOffset = canvas.height / 2 - ((lines.length - 1) * lineHeight) / 2;
	
		lines.forEach((line, i) => {
			ctx.fillText(line, canvas.width / 2, yOffset + i * lineHeight);
		});
	
		const texture = new THREE.CanvasTexture(canvas);
		texture.needsUpdate = true;
		return texture;
	}

	// Helper function to dispose of a mesh and remove from scene
	const disposeMesh = (mesh: THREE.Mesh | null) => {
		if (!mesh || !sceneRef.current) return;

		sceneRef.current.remove(mesh);
		mesh.geometry.dispose();
		disposeMaterials(mesh.material);
	};

	// Create arrow plane for the top face
	const createArrowPlane = () => {
		if (!sceneRef.current || !arrowTexture) return null;

		// Create a new material with the arrow texture
		const arrowMaterial = new THREE.MeshBasicMaterial({
			map: arrowTexture,
			transparent: true,
			opacity: 0.9,
			side: THREE.DoubleSide,
			depthWrite: false, // Prevents z-fighting
		});

		// Create a plane for the arrow slightly above the top face
		const arrowPlaneGeometry = new THREE.PlaneGeometry(width * 0.8, length * 0.8);
		const arrowPlane = new THREE.Mesh(arrowPlaneGeometry, arrowMaterial);

		// Position the arrow plane just above the top face
		arrowPlane.position.set(0, height / 2 + 0.05, 0); // Slightly above the top face
		arrowPlane.rotation.x = -Math.PI / 2; // Rotate to lie flat on the top face
		arrowPlane.rotation.z = Math.PI; // Ensure arrow points to front (+Z)

		sceneRef.current.add(arrowPlane);
		return arrowPlane;
	};

	// Create text labels for all faces
	const createTextLabels = () => {
		if (!sceneRef.current || textTextures.length === 0) return null;

		const textGroup = new THREE.Group();
		sceneRef.current.add(textGroup);

		// Define face positions and rotations
		const faceConfigs: FaceConfig[] = [
			{
				// Right face (+X)
				width: height,
				height: length,
				pos: new THREE.Vector3(width / 2 + 0.5, 0, 0),
				rot: new THREE.Euler(0, Math.PI / 2, 0),
			},
			{
				// Left face (-X)
				width: height,
				height: length,
				pos: new THREE.Vector3(-width / 2 - 0.5, 0, 0),
				rot: new THREE.Euler(0, -Math.PI / 2, 0),
			},
			{
				// Front face (+Z)
				width: width,
				height: height,
				pos: new THREE.Vector3(0, 0, length / 2 + 0.5),
				rot: new THREE.Euler(0, 0, 0),
			},
			{
				// Back face (-Z)
				width: width,
				height: height,
				pos: new THREE.Vector3(0, 0, -length / 2 - 0.5),
				rot: new THREE.Euler(0, Math.PI, 0),
			},
		];

		// Create text overlays
		textTextures.forEach((texture, i) => {
			const textMaterial = new THREE.MeshBasicMaterial({
				map: texture,
				transparent: true,
				opacity: 0.9,
				side: THREE.FrontSide,
			});

			const config = faceConfigs[i];
			const textPlaneGeom = new THREE.PlaneGeometry(config.width, config.height);
			const textPlane = new THREE.Mesh(textPlaneGeom, textMaterial);

			textPlane.position.copy(config.pos);
			textPlane.rotation.copy(config.rot);

			textGroup.add(textPlane);
		});

		return textGroup;
	};

	// Create and add box to scene when scene is ready or when dimensions change
	useEffect(() => {
		if (!sceneRef.current) return;

		// Clean up previous meshes
		disposeMesh(boxMeshRef.current);

		// Clean up text group
		if (textGroupRef.current && sceneRef.current) {
			textGroupRef.current.traverse(child => {
				if (child instanceof THREE.Mesh) {
					child.geometry.dispose();
					disposeMaterials(child.material);
				}
			});
			sceneRef.current.remove(textGroupRef.current);
			textGroupRef.current = null;
		}

		disposeMesh(arrowPlaneRef.current);

		const boxGeometry = new THREE.BoxGeometry(width, height, length);

		const boxMaterials = getBoxMaterials(
			getFaceMap(labelPosition),
			assets.cardboardMaterial!,
			assets.labelTextures!,
			assets.emissiveMap!,
			false
		);

		const boxMesh = new THREE.Mesh(boxGeometry, boxMaterials);
		sceneRef.current.add(boxMesh);
		boxMeshRef.current = boxMesh;

		// Add arrow on top face if enabled
		if (showFaceLabels && arrowTexture) {
			arrowPlaneRef.current = createArrowPlane();
		}

		// Add text labels if enabled
		if (showFaceLabels && textTextures.length > 0) {
			textGroupRef.current = createTextLabels();
		}

		// Cleanup function
		return () => {
			if (sceneRef.current) {
				// Remove all objects from the scene
				disposeMesh(boxMeshRef.current);
				boxMeshRef.current = null;

				if (textGroupRef.current) {
					textGroupRef.current.traverse(child => {
						if (child instanceof THREE.Mesh) {
							child.geometry.dispose();
							disposeMaterials(child.material);
						}
					});
					sceneRef.current.remove(textGroupRef.current);
					textGroupRef.current = null;
				}

				disposeMesh(arrowPlaneRef.current);
				arrowPlaneRef.current = null;
			}
		};
	}, [
		sceneRef,
		width,
		length,
		height,
		labelPosition,
		showFaceLabels,
		textTextures,
		arrowTexture,
	]);

	useEffect(() => {
		if(!sceneRef.current || sceneRef.current.scale.x < 1) return; //already scaled down
		sceneRef.current?.scale.setScalar(0.33);
	}, []);

	return (
		<div
			ref={mountRef}
			style={{
				width: '100%',
				height: '100%',
			}}
		/>
	);
}
